/**
 * Custom hook for polling application state data from the API.
 *
 * This hook continuously fetches quote data for a given policy upload form ID
 * until processing is complete. It prevents duplicate polling requests for the
 * same ID and manages loading/error states in the global context. While the form
 * is computing quotes it can hand off to an SSE stream (opt in via
 * `streamQuotesProductType`) so quotes appear in real time.
 *
 * @param params - Configuration parameters
 * @param params.id - Policy upload form ID to poll
 * @param params.enabled - Whether polling should be active
 * @param params.key - Key to differentiate polling sessions (triggers restart)
 * @param params.streamQuotesProductType - Product type to stream while PROCESSING_QUOTES
 */

import { useEffect, useRef } from 'react';
import { useAppContext } from '@/app/providers/AppContextProvider';
import apiService from '@/services/apiService';
import { isEqual } from '@/services/utils';
import type { AppData, Quote, QuoteListResponse } from '@/types/app';
import statusConfig from '../../config/status.json';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Interval between polling requests in milliseconds */
const POLLING_INTERVAL = import.meta.env.VITE_POLLING_INTERVAL
	? Number.parseInt(import.meta.env.VITE_POLLING_INTERVAL, 10)
	: 2500;

/** Maximum number of polling attempts before giving up */
const MAX_POLLING_ATTEMPTS = 99;

/** Status emitted while insurers are still being queried for quotes. */
const PROCESSING_QUOTES_STATUS = 'PROCESSING_QUOTES';

/**
 * Merges a streamed `QuoteListResponse` snapshot into the existing `AppData`.
 *
 * The per-product-type stream payload is filtered to a single product type, but
 * it carries the same form-level fields the full GET does (status, company info,
 * current policy, premium variables). We spread `prev` first, then overlay those
 * fields and the streamed product type's quotes — merging `quotes` by key so
 * other product types already loaded are preserved.
 */
export const mergeStreamSnapshot = (
	prev: AppData | null,
	snapshot: QuoteListResponse,
	productType: string
): AppData => {
	// `QuoteListResponse.quotes` is typed as `QuoteItem[]`; `AppData.quotes` uses
	// the structurally-equivalent `Quote[]`. Same backend payload, two frontend
	// declarations — bridge them here rather than fanning the cast out to callers.
	// `AppData.quotes` has no string index signature, so index it as a record.
	const prevQuotes = (prev?.quotes ?? {}) as Record<string, unknown>;
	const streamedQuotes = snapshot.quotes?.[productType] as unknown as Quote[] | undefined;
	return {
		...(prev ?? {}),
		status: snapshot.status ?? prev?.status,
		expected_num_quotes: snapshot.expected_num_quotes ?? prev?.expected_num_quotes,
		policy_warnings: snapshot.policy_warnings ?? prev?.policy_warnings,
		relevant_product_types: snapshot.relevant_product_types ?? prev?.relevant_product_types,
		company_info: snapshot.company_info ?? prev?.company_info,
		current_policy: snapshot.current_policy ?? prev?.current_policy,
		premium_variables: snapshot.premium_variables ?? prev?.premium_variables,
		// The add-later flow settles via this stream (the terminal snapshot arrives
		// right before `done`), so these two must overlay or the sidebar stays stale
		// until a full GET (reload / step nav): the inline upload error never shows,
		// and the "add your policy later" CTA never hides after a success. Unlike the
		// fields above, `unreadable_reason` is taken authoritatively from the snapshot
		// (not `?? prev`) because `null` is meaningful — a successful upload clears it,
		// and a `?? prev` fallback would wrongly keep the previous error.
		unreadable_reason: snapshot.unreadable_reason ?? null,
		has_current_policy_document:
			snapshot.has_current_policy_document ?? prev?.has_current_policy_document,
		quotes: {
			...prevQuotes,
			// The snapshot is authoritative for the streamed product type: an empty /
			// absent list means the backend has no quotes for it right now (e.g. stale
			// quotes were cleared for a re-fetch), so clear them rather than keeping the
			// previous ones. Other product types stay put via the spread above.
			[productType]: streamedQuotes ?? []
		} as AppData['quotes']
	};
};

/**
 * Keep a locally-imported/edited (but not-yet-saved) employee list from being
 * clobbered by an incoming server snapshot.
 *
 * The employee import — and the wizard's manual edits — only update local state;
 * the actual save happens when the user confirms the step. While quotes are still
 * computing, though, every poll/stream snapshot carries the server's *last saved*
 * premium variables, which do not yet include those optimistic edits. Applying such
 * a snapshot wholesale wipes the just-imported employees (issue #796: the grid stays
 * empty on the first import, then "works" on a retry only because by then the quote
 * has finished computing and polling has stopped).
 *
 * A snapshot only carries a genuine employee update when its `employees` object
 * differs from the previous server snapshot's. When they are equal the incoming
 * payload is just quote/status progress, so we keep whatever employees are currently
 * in state (preserving the local edit). We only let the server overwrite them when it
 * actually reports a different `employees` object — e.g. the post-save recalculation
 * echoing the now-trusted list, which must win so the source/trust flips through.
 */
export const reconcileEmployees = (
	incoming: AppData,
	current: AppData | null,
	lastServer: AppData | null
): AppData => {
	const currentEmployees = current?.premium_variables?.employees;
	// Nothing edited locally yet (or first load) — take the server snapshot as-is.
	if (!currentEmployees) return incoming;

	const serverChangedEmployees = !isEqual(
		(incoming.premium_variables?.employees ?? null) as Record<string, unknown> | null,
		(lastServer?.premium_variables?.employees ?? null) as Record<string, unknown> | null
	);
	if (serverChangedEmployees) return incoming;

	return {
		...incoming,
		premium_variables: {
			...incoming.premium_variables,
			employees: currentEmployees
		}
	};
};

// =============================================================================
// GLOBAL STATE MANAGEMENT
// =============================================================================

/**
 * Tracks active polling sessions to prevent duplicate requests.
 * Uses composite keys in format: `${id}-${key}` to allow multiple
 * sessions per ID when needed (e.g., after data mutations).
 */
const activePollers = new Set<string>();

/**
 * Stores the most recent data response to detect changes.
 * Reset when ID changes to ensure fresh data comparison.
 */
let previousData: Record<string, unknown> | null = null;

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

interface UseAppStatePollingParams {
	id: string | undefined;
	enabled: boolean;
	key?: number;
	/**
	 * When set, the loop streams this product type's quotes via SSE while the
	 * form is in `PROCESSING_QUOTES` instead of polling, so quotes appear in real
	 * time. Falls back to polling if the stream drops or errors. Leave undefined
	 * (the default) for poll-only behaviour.
	 */
	streamQuotesProductType?: string;
}

export function useAppStatePolling({
	id,
	enabled,
	key = 0,
	streamQuotesProductType
}: UseAppStatePollingParams): void {
	const context = useAppContext();
	if (!context) {
		throw new Error('useAppStatePolling must be used within AppContextProvider');
	}
	const { setAppData, setIsLoading, setIsError } = context;
	const pollerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Reset previous data when ID changes to ensure fresh comparison
	useEffect(() => {
		previousData = null;
	}, []);

	useEffect(() => {
		// Early return if polling is disabled or no ID provided
		if (!enabled || !id) {
			return;
		}

		// Create unique key for this polling session
		const pollingKey = `${id}-${key}`;

		// Prevent duplicate polling for the same ID+key combination
		if (activePollers.has(pollingKey)) {
			return;
		}

		// Mark this polling session as active
		activePollers.add(pollingKey);

		// NB: `previousData` (the module-level de-dupe baseline) is deliberately NOT
		// reset here on restart — it doubles as the "last server snapshot" that
		// `reconcileEmployees` diffs against to tell a real employee update apart from
		// plain quote/status progress. Resetting it would make the next snapshot look
		// like a genuine employee change and clobber a not-yet-saved import (#796).

		// Control flag for cleanup and preventing race conditions
		let isActive = true;
		let attempts = 0;

		// SSE hand-off state. The stream is opened at most once per polling session:
		// while it is live we don't schedule polls (no double-fetch), and after it
		// ends (done / drop / error) we recover via the poll loop rather than
		// re-opening. So the page makes ONE stream connection that carries many
		// quote events, not a new connection per event.
		let streamAttempted = false;
		let streamController: AbortController | null = null;

		/**
		 * Performs a single polling request and schedules the next one if needed.
		 * Updates global state with new data and handles completion/error states.
		 */
		async function poll() {
			// Stop polling if component unmounted or max attempts reached
			if (!isActive || attempts >= MAX_POLLING_ATTEMPTS) {
				return;
			}

			// Set loading state before API call
			setIsLoading(true);

			// Guard against undefined id (already checked in useEffect, but TypeScript doesn't know)
			if (!id) {
				setIsLoading(false);
				return;
			}

			try {
				// Fetch latest quote data from API
				const data = await apiService.quote.sick_leave_short_term.fetch(id);

				// Only update state if data has actually changed
				if (!isEqual(data as Record<string, unknown> | null, previousData)) {
					// Preserve an optimistic, not-yet-saved employee import/edit that this
					// snapshot would otherwise wipe (issue #796).
					const lastServer = previousData as AppData | null;
					setAppData((prev) => reconcileEmployees(data, prev, lastServer));
					previousData = data;
				}

				// Clear loading state
				setIsLoading(false);

				// Check if processing is complete (based on status configuration)
				if (data.status && statusConfig.groups.isComplete.includes(data.status)) {
					activePollers.delete(pollingKey);
					return; // Stop polling
				}

				// Hand off to the SSE stream while quotes are being computed: it pushes
				// quotes in real time instead of waiting for the next poll. Only the
				// first PROCESSING_QUOTES poll opens a stream; once attempted we stay on
				// the poll loop (see streamAttempted).
				if (
					streamQuotesProductType &&
					data.status === PROCESSING_QUOTES_STATUS &&
					!streamAttempted
				) {
					streamQuotes(streamQuotesProductType);
					return; // the stream owns updates now; do not schedule a poll
				}

				// Schedule next polling attempt
				attempts += 1;
				pollerRef.current = setTimeout(poll, POLLING_INTERVAL);
			} catch (error) {
				// Handle polling errors with user-friendly message
				setIsError('Het is niet gelukt om je aanbod te laden. Probeer het later opnieuw...');
				setIsLoading(false);
				activePollers.delete(pollingKey);
				console.error('Failed to poll application state', error);
			}
		}

		// Stream quotes for the given product type, merging each snapshot into
		// AppData so quotes appear in real time. The stream is not trusted to leave
		// the page in its final state: whether it ends with `done` or drops/fails,
		// we hand back to the poll loop for one authoritative GET. That GET clears
		// loading and stops on a terminal status, so the page never gets stuck
		// loading if the backend emits `done` before flipping the form status (SSE
		// gate: snapshot + immediate `done` when `quote_fetch_statuses[type]` is
		// absent).
		function streamQuotes(productType: string) {
			if (!id) return;
			streamAttempted = true;
			const controller = new AbortController();
			streamController = controller;
			let doneReceived = false;

			const fallbackToPolling = () => {
				if (doneReceived || controller.signal.aborted || !isActive) return;
				// Stream dropped/failed before `done` — recover via the poll loop.
				// We never re-open the stream (streamAttempted stays true).
				pollerRef.current = setTimeout(poll, POLLING_INTERVAL);
			};

			apiService.quote.byProductType
				.stream(
					id,
					productType,
					{
						onQuotes: (snapshot) => {
							if (!isActive) return;
							// Same shield as the poll path: a streamed snapshot carries the
							// server's last-saved premium variables, so preserve an optimistic,
							// not-yet-saved employee import/edit it would otherwise wipe (#796).
							const lastServer = previousData as AppData | null;
							setAppData((prev) =>
								reconcileEmployees(
									mergeStreamSnapshot(prev, snapshot, productType),
									prev,
									lastServer
								)
							);
						},
						onDone: () => {
							doneReceived = true;
							controller.abort(); // close the long-lived SSE connection
							if (!isActive) return;
							// Confirm the authoritative status via one poll. poll() owns
							// setIsLoading / activePollers — it clears loading after the GET and
							// stops on a terminal status (and keeps polling otherwise, in case
							// `done` arrived before the backend flipped the form status).
							pollerRef.current = setTimeout(poll, 0);
						}
					},
					controller.signal
				)
				// Stream closed without `done` (dropped connection) or rejected
				// (transport error / abort) — fall back to polling unless we
				// already finished, were aborted, or the session is stale.
				.then(fallbackToPolling, fallbackToPolling);
		}

		// Defer the first poll so Strict Mode cleanup can cancel it before it fires
		pollerRef.current = setTimeout(poll, 0);

		// Cleanup function to prevent memory leaks and race conditions
		return () => {
			isActive = false;
			activePollers.delete(pollingKey);
			if (pollerRef.current) {
				clearTimeout(pollerRef.current);
			}
			// Close any open SSE connection on unmount / key-restart.
			streamController?.abort();
		};

		// Dependencies: restart polling when any of these change
	}, [id, enabled, key, streamQuotesProductType, setAppData, setIsLoading, setIsError]);
}
